/**
 * Persist Google Calendar event IDs per match per user.
 * Used to update calendar events when matches are rescheduled.
 * Optional: one shared event per match with attendees for RSVP.
 */

import { doc, getDoc, setDoc, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

function getParticipantIds(match) {
  if (!match) return [];
  if (match.matchType === 'doubles') {
    return [...(match.team1Players || []), ...(match.team2Players || [])];
  }
  return [match.player1Id, match.player2Id].filter(Boolean);
}

/**
 * Get participant emails for a match (players + referee). Used to create calendar event with attendees for RSVP.
 * @param {object} match - Match with player1Id, player2Id or team1Players, team2Players, refereeId
 * @returns {Promise<string[]>} Emails (deduplicated, no empty)
 */
export async function getParticipantEmailsForMatch(match) {
  const ids = new Set(getParticipantIds(match));
  if (match.refereeId) ids.add(match.refereeId);
  const emails = [];
  for (const uid of ids) {
    if (!uid) continue;
    try {
      const snap = await getDoc(doc(db, 'users', uid));
      if (snap.exists() && snap.data().email) emails.push(snap.data().email.trim());
    } catch {
      // skip
    }
  }
  return [...new Set(emails)].filter(Boolean);
}

function docId(matchId, userId) {
  return `${matchId}_${userId}`;
}

/**
 * Save a calendar event record after creating an event.
 */
export async function saveMatchCalendarEvent(matchId, userId, eventId, scheduledAtMs) {
  const ref = doc(db, 'matchCalendarEvents', docId(matchId, userId));
  await setDoc(ref, {
    matchId,
    userId,
    eventId,
    calendarId: 'primary',
    scheduledAtMs: scheduledAtMs ?? null,
    updatedAt: new Date(),
  }, { merge: true });
}

/**
 * Get a user's calendar event record for a match.
 * @returns {{ eventId: string, scheduledAtMs: number } | null}
 */
export async function getMatchCalendarEvent(matchId, userId) {
  const ref = doc(db, 'matchCalendarEvents', docId(matchId, userId));
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const d = snap.data();
  return {
    eventId: d.eventId,
    scheduledAtMs: d.scheduledAtMs ?? null,
  };
}

/**
 * Update stored scheduledAtMs after a calendar event was updated (match rescheduled).
 */
export async function updateMatchCalendarEventScheduledAt(matchId, userId, scheduledAtMs) {
  const ref = doc(db, 'matchCalendarEvents', docId(matchId, userId));
  await setDoc(ref, {
    scheduledAtMs: scheduledAtMs ?? null,
    updatedAt: new Date(),
  }, { merge: true });
}

/**
 * List all match calendar events for a user (to detect deleted matches and remove events).
 * @param {string} userId
 * @returns {Promise<Array<{ matchId: string, eventId: string }>>}
 */
export async function getMatchCalendarEventsForUser(userId) {
  if (!userId) return [];
  const q = query(
    collection(db, 'matchCalendarEvents'),
    where('userId', '==', userId)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data();
    return { matchId: data.matchId || d.id.split('_')[0], eventId: data.eventId };
  }).filter((r) => r.eventId);
}

/**
 * Remove a user's calendar event record (after deleting the event from Google Calendar).
 */
export async function deleteMatchCalendarEvent(matchId, userId) {
  const ref = doc(db, 'matchCalendarEvents', docId(matchId, userId));
  await deleteDoc(ref);
}
